import React, { useEffect, useRef, useState } from 'react';
import KeywordFormSection from './components/KeywordFormSection';
import styles from './HighlightsPanel.module.scss';
import { Checkbox, Link, Loading, Message, Text } from 'components/core';
import { Inline, Stack } from 'components/layout';
import { SidebarBody } from 'components/shared/Sidebar';
import {
  useHighlights,
  useOnHighlightsSaved,
} from 'hooks/reviews/useHighlights';
import usePersistedState from 'hooks/usePersistedState';
import { HighlightsResult } from 'query/review/Highlights';

const apiErrorMessage =
  'Error saving changes. Please try again or refresh the page.';

export type StudyListDisplayOptions = {
  showAbstracts: boolean;
  toggleAbstracts: () => void;
  toggleHighlights: () => void;
  enableHighlights: () => void;
};

type Props = {
  layout?: 'modal' | 'sidebar';
  studyListDisplayOptions?: StudyListDisplayOptions;
  pollForUpdates?: boolean;
};

export const HighlightsPanel = ({
  layout = 'modal',
  studyListDisplayOptions,
  pollForUpdates = false,
}: Props) => {
  const {
    showAbstracts = false,
    toggleAbstracts,
    toggleHighlights,
    enableHighlights,
  } = studyListDisplayOptions || {};
  const [hasAPIError, setHasAPIError] = useState(false);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const isStacked = layout === 'sidebar';

  // Use 1 & 0 instead of true & false for backwards compatibility with legacy Backbone code
  const [
    isShowHighlightsChecked,
    setIsShowHighlightsChecked,
  ] = usePersistedState(
    'covidence.userPreferences.keywordHighlightingEnabled',
    1
  );

  const isHighlightsPresent = (highlights: HighlightsResult | undefined) => {
    return (
      (highlights?.includedKeywords?.length ?? 0) > 0 ||
      (highlights?.excludedKeywords?.length ?? 0) > 0
    );
  };

  const { highlights, isLoading } = useHighlights({
    poll: pollForUpdates,
  });
  const currentHighlights = useRef(highlights);

  useOnHighlightsSaved((newHighlights) => {
    const isAddingFirstHighlight =
      !isHighlightsPresent(currentHighlights.current) &&
      isHighlightsPresent(newHighlights);

    currentHighlights.current = newHighlights;

    if (isAddingFirstHighlight) {
      enableHighlights?.();
      setIsShowHighlightsChecked(1);
    }
  });

  useEffect(() => {
    if (hasAPIError) {
      errorRef.current?.focus();
    }
  }, [hasAPIError]);

  const keywordSections = (
    <>
      <KeywordFormSection
        title="Inclusion"
        titleColor="success"
        label="Add inclusion word"
        type="included"
        onHighlightChangeError={() => setHasAPIError(true)}
        onHighlightChangeSuccess={() => setHasAPIError(false)}
      />
      <div className={styles.keywordSectionDivider} />
      <KeywordFormSection
        title="Exclusion"
        titleColor="danger"
        label="Add exclusion word"
        type="excluded"
        onHighlightChangeError={() => setHasAPIError(true)}
        onHighlightChangeSuccess={() => setHasAPIError(false)}
      />
    </>
  );

  if (isLoading) {
    return (
      <Inline>
        <Loading size="xl" className={styles.loadingSpinner} />
      </Inline>
    );
  }

  const panel = (
    <>
      <Stack spacing={5}>
        {studyListDisplayOptions ? (
          <Stack
            component="fieldset"
            spacing={2}
            className={styles.showOptions}
          >
            <Checkbox
              aria-label="Show abstracts"
              checked={showAbstracts}
              onChange={() => toggleAbstracts?.()}
              data-pendo-key={
                showAbstracts ? 'hide-abstracts' : 'show-abstracts'
              }
            >
              Show abstracts
            </Checkbox>
            {isHighlightsPresent(highlights) && (
              <Checkbox
                aria-label="Show highlights"
                checked={isShowHighlightsChecked == 1}
                onChange={() => {
                  toggleHighlights?.();
                  setIsShowHighlightsChecked(
                    isShowHighlightsChecked == 1 ? 0 : 1
                  );
                }}
                data-pendo-key={
                  isShowHighlightsChecked == 1
                    ? 'hide-highlights'
                    : 'show-highlights'
                }
              >
                Show highlights
              </Checkbox>
            )}
          </Stack>
        ) : (
          <Text size="sm" variant="neutral">
            Highlights help to quickly spot words or phrases during screening by
            automatically highlighting inclusion words in green and exclusion
            words in red.{' '}
            <Link
              external
              variant="primary"
              to="https://support.covidence.org/help/how-highlighting-works-in-covidence"
            >
              Learn more about how highlighting works
            </Link>
          </Text>
        )}

        {isStacked ? (
          <Stack spacing={5}>{keywordSections}</Stack>
        ) : (
          <Inline alignY="top" shouldWrap spacing={5}>
            {keywordSections}
          </Inline>
        )}
      </Stack>

      <div role="alert" ref={errorRef} tabIndex={-1}>
        {hasAPIError && (
          <Message
            className={styles.apiErrorMessage}
            type="danger"
            variant="prominent"
            isVisible={hasAPIError}
            onClose={() => setHasAPIError(false)}
          >
            <Text>{apiErrorMessage}</Text>
          </Message>
        )}
      </div>
    </>
  );

  return isStacked ? <SidebarBody>{panel}</SidebarBody> : panel;
};
